import { Component, inject, Input } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ENVIRONMENT } from '@core/core.constants';
import { PaymentMethod } from '@core/models/common.model';
import { Edition, ExtraService, Rate, getRatePrice } from '@core/models/edition.model';
import { Inscription } from '@core/models/inscription.model';
import { TPVService } from '@core/services/tpv.service';
import { UiService } from '@core/services/ui.service';
import { EditionService } from '@core/use-case-services/edition.service';
import { InscriptionService } from '@core/use-case-services/inscription.service';
import { NavController } from '@ionic/angular';
import { combineLatest, first, map } from 'rxjs';

@Component({
  selector: 'gt-register-step2',
  templateUrl: './payment.page.html',
  styleUrls: ['./payment.page.scss'],
})
export class PaymentPage {
  @Input() showHeader = true;
  loading = false;
  navController = inject(NavController);
  editionService = inject(EditionService);
  participantInscriptionService = inject(InscriptionService);
  route = inject(ActivatedRoute);
  tpvService = inject(TPVService);
  uiService = inject(UiService);
  environment = inject(ENVIRONMENT);

  public paymentMethod: PaymentMethod | null = null;
  public paymentOption: 'all' | 'registration' = 'all';
  private savedOptionalServices: { id: string; quantity: number }[] = [];

  public total = 0;
  public currentRate: string | null = null;
  public currentRatePrice = 0;
  public services: { id: string; quantity: number; price: number }[] = [];
  public editionExtraServices: ExtraService[] = [];

  private myCurrentInscription$ = this.participantInscriptionService.myCurrentInscription$;
  private currentEdition$ = this.editionService.currentEdition$;

  public data$ = combineLatest([this.myCurrentInscription$, this.currentEdition$]).pipe(
    map(([inscription, edition]) => {
      if (!inscription) throw new Error('Inscription not found on selecting payment method');
      return { inscription, edition };
    }),
  );

  public extraServices$ = this.data$.pipe(
    map(({ inscription, edition }) => {
      this.services = edition.extraServices.map((s) => ({ id: s.id, quantity: 0, price: s.price }));
      this.editionExtraServices = edition.extraServices;
      const extraServices = edition.extraServices.map((extraService) => {
        return {
          ...extraService,
          maxQuantity: extraService.byPerson
            ? (inscription.summonedParticipants?.length ?? 0)
            : extraService.maxQuantity,
        };
      });

      const rates = edition.rates.filter(
        (rate) =>
          rate.startDate < new Date() && new Date() < rate.endDate && rate.categories.includes(inscription.category),
      );
      this.currentRate = rates[0].id;
      this.currentRatePrice = getRatePrice(rates[0], inscription.level, edition.levelPricesEnabled);
      this.recalculateTotal(inscription, edition);

      // Sort services: mandatory first, then optional
      return extraServices.sort((a, b) => {
        if (a.mandatory && !b.mandatory) return -1;
        if (!a.mandatory && b.mandatory) return 1;
        return 0;
      });
    }),
  );

  recalculateTotal(inscription: Inscription, edition: Edition) {
    const rates = edition.rates.filter(
      (rate: Rate) =>
        rate.startDate < new Date() && new Date() < rate.endDate && rate.categories.includes(inscription.category),
    );
    let baseTotal = rates[0] ? getRatePrice(rates[0], inscription.level, edition.levelPricesEnabled) : 0;

    if (inscription.isPremium) {
      const premiumPack = edition.premiumPacks.find((pack: { id: string }) => pack.id === inscription.premiumPack?.id);
      baseTotal += premiumPack?.price ?? 0;
    }

    if (this.paymentOption === 'all') {
      // Include all mandatory services + selected optional services
      const mandatoryServicesTotal = this.editionExtraServices
        .filter((s) => s.mandatory && !s.payOffline)
        .reduce((sum, s) => {
          // For mandatory services, calculate the quantity based on byPerson
          const quantity = s.byPerson ? (inscription.summonedParticipants?.length ?? 1) : 1;
          return sum + s.price * quantity;
        }, 0);

      const optionalServicesTotal = this.services
        .filter((s) => {
          const extraService = this.editionExtraServices.find((es) => es.id === s.id);
          return extraService && !extraService.mandatory && s.quantity > 0;
        })
        .reduce((sum, s) => {
          // For optional services, use the quantity set by the user
          return sum + s.price * s.quantity;
        }, 0);

      this.total = baseTotal + mandatoryServicesTotal + optionalServicesTotal;
    } else {
      // Only registration (rate + premium pack)
      this.total = baseTotal;
    }
  }

  onPaymentOptionChange() {
    this.data$.pipe(first()).subscribe(({ inscription, edition }) => {
      if (this.paymentOption === 'registration') {
        // Save current optional services quantities
        this.savedOptionalServices = this.services
          .filter((s) => {
            const extraService = this.editionExtraServices.find((es) => es.id === s.id);
            return extraService && !extraService.mandatory && s.quantity > 0;
          })
          .map((s) => ({ id: s.id, quantity: s.quantity }));

        // Reset optional services quantities to 0
        this.services.forEach((s) => {
          const extraService = this.editionExtraServices.find((es) => es.id === s.id);
          if (extraService && !extraService.mandatory) {
            s.quantity = 0;
          }
        });
      } else {
        // Restore saved optional services quantities
        this.savedOptionalServices.forEach((saved) => {
          const service = this.services.find((s) => s.id === saved.id);
          if (service) {
            service.quantity = saved.quantity;
          }
        });
      }

      this.recalculateTotal(inscription, edition);
    });
  }

  reCalcRate() {
    // Simply recalculate the total based on current state
    this.data$.pipe(first()).subscribe(({ inscription, edition }) => {
      this.recalculateTotal(inscription, edition);
    });
  }

  getServiceByExtraServiceId(extraServiceId: string): { id: string; quantity: number; price: number } | undefined {
    return this.services.find((s) => s.id === extraServiceId);
  }

  updateServiceQuantity(extraServiceId: string, quantity: number) {
    const service = this.services.find((s) => s.id === extraServiceId);
    if (service) {
      service.quantity = quantity;
      this.reCalcRate();
    }
  }

  async confirm() {
    if (this.loading) return;
    this.loading = true;
    await this.uiService.showLoading();
    this.data$.pipe(first()).subscribe(({ edition, inscription }) => {
      // Prepare all extra services with appropriate quantities (excluding payOffline services)
      const extraServices = this.editionExtraServices
        .filter((extraService) => !extraService.payOffline)
        .map((extraService) => {
          if (extraService.mandatory) {
            // Mandatory services: always include with calculated quantity
            return {
              id: extraService.id,
              quantity: extraService.byPerson ? (inscription.summonedParticipants?.length ?? 1) : 1,
              price: extraService.price,
            };
          } else {
            // Optional services: include with selected quantity or 0
            const service = this.services.find((s) => s.id === extraService.id);
            const quantity = this.paymentOption === 'all' ? (service?.quantity ?? 0) : 0;
            return {
              id: extraService.id,
              quantity,
              price: extraService.price,
            };
          }
        });

      this.tpvService
        .createPaymentIntent({
          inscriptionId: inscription.id,
          editionId: edition.id,
          extraServices,
          rate: this.currentRate,
          appType: this.environment.appType,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          type: this.paymentMethod!,
          paymentOption: this.paymentOption,
        })
        .then(() => {
          setTimeout(() => this.uiService.hideLoading(), 5_000);
          this.loading = false;
        })
        .catch(() => {
          this.uiService.hideLoading();
          this.loading = false;
        });
    });
  }
}
